import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AxisPropertiesFields from "./AxisPropertiesFields";
import { useLidarClearance } from "./LidarClearanceContext";
import {
  type AxisProperties,
  createAxis,
  isValidAxisProperties,
} from "./api";
import t from "./translations";

const DEFAULT_PROPERTIES: AxisProperties = {
  name_en: "",
  min_value: 0,
  max_value: 10,
  color_scale: 2,
  threshold_1: 9,
  threshold_2: 7,
  threshold_3: 5,
  threshold_4: 3,
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CreateAxisDialog({ open, onClose }: Props) {
  const { regionTags, refetch } = useLidarClearance();
  const [region, setRegion] = useState("");
  const [properties, setProperties] =
    useState<AxisProperties>(DEFAULT_PROPERTIES);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRegion(regionTags[0] ?? "");
      setProperties(DEFAULT_PROPERTIES);
    }
  }, [open, regionTags]);

  const canSubmit =
    region.length > 0 && isValidAxisProperties(properties) && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createAxis(region, {
        ...properties,
        name_en: properties.name_en.trim(),
      });
      await refetch();
      onClose();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.createAxis" });
      toast.error(t.FailedToCreateAxis);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      fullWidth
    >
      <DialogTitle>{t.CreateAxisDialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t.RegionLabel}
            value={region}
            onChange={(event) => setRegion(event.target.value)}
            disabled={submitting}
          >
            {regionTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            label={t.NameEnLabel}
            helperText={t.NameVisibleToCustomersNote}
            value={properties.name_en}
            onChange={(event) =>
              setProperties({ ...properties, name_en: event.target.value })
            }
            disabled={submitting}
          />
          <AxisPropertiesFields
            region={region}
            value={properties}
            onChange={setProperties}
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t.CancelButton}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t.CreateButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
